import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Breakpoint, DrawerProps, SxProps, Theme } from '@mui/material';

import { useScreenSize } from '../../../hooks/useScreenSize';
import { myLocalStorage } from '../../../myLocalStorage';
import { AppBarHeadingProvider } from '../AppBarHeading/AppBarHeadingProvider';
import { MainLayoutContext } from './MainLayoutContext';
import { MainLayoutContextType } from './types';

const DRAWER_WIDTH = 264;
const COLLAPSED_DRAWER_WIDTH = 72;

const LOCAL_STORAGE_KEY = 'isDrawerOpen';

const getIsDefaultOpen = (currentBreakpoint: Breakpoint, drawerVariant: DrawerProps['variant']) => {
  const isDrawerOpenFromStorage: 'true' | 'false' | undefined =
    myLocalStorage.getItem(LOCAL_STORAGE_KEY);
  if (drawerVariant === 'permanent' && isDrawerOpenFromStorage !== undefined) {
    return isDrawerOpenFromStorage === 'true';
  }

  return currentBreakpoint === 'lg' || currentBreakpoint === 'xl';
};

export const MainLayoutProvider: FC<PropsWithChildren> = ({ children }) => {
  const navigationDrawerPaperId = useId();
  const navigationMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const { isDesktop, currentBreakpoint } = useScreenSize();

  const drawerVariant: DrawerProps['variant'] = isDesktop ? 'permanent' : 'temporary';

  const [isDrawerOpen, setIsDrawerOpen] = useState(
    getIsDefaultOpen(currentBreakpoint, drawerVariant)
  );

  const location = useLocation();
  const initialLocation = useRef(location);

  useEffect(() => {
    if (drawerVariant === 'permanent' || initialLocation.current === location) {
      return () => {};
    }

    setIsDrawerOpen(false);
  }, [drawerVariant, location]);

  const isCollapsed = isDesktop && !isDrawerOpen;

  const drawerWidth = isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;

  const drawerSx: SxProps<Theme> = useMemo(
    () => (theme) => ({
      px: 2,
      pb: 2,
      border: 'none',
      width: drawerWidth,
      zIndex: drawerVariant === 'permanent' ? 0 : theme.zIndex.drawer,
      transition: theme.transitions.create(['width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      ...(isDrawerOpen && {
        transition: theme.transitions.create(['width'], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }),
    }),
    [drawerVariant, drawerWidth, isDrawerOpen]
  );

  const containerSx: SxProps<Theme> = useMemo(
    () => (theme) => ({
      borderRadius: 0,
      borderTopLeftRadius: drawerVariant === 'permanent' ? 16 : 0,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      ...(isDesktop && {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }),
    }),
    [drawerVariant, drawerWidth, isDesktop]
  );

  const toggleDrawer = useCallback(
    (isOpen?: boolean) => {
      setIsDrawerOpen((prev) => {
        const open = isOpen !== undefined ? isOpen : !prev;

        if (drawerVariant === 'permanent') {
          myLocalStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(open));
        }

        return open;
      });
    },
    [drawerVariant]
  );

  const ctx = useMemo<MainLayoutContextType>(
    () => ({
      drawerVariant,
      isDrawerCollapsed: isCollapsed,
      isDrawerOpen,
      containerSx,
      drawerSx,
      navigationDrawerPaperId,
      navigationMenuButtonRef,
      toggleDrawer,
    }),
    [drawerVariant, isCollapsed, isDrawerOpen, containerSx, drawerSx, navigationDrawerPaperId, toggleDrawer]
  );

  return (
    <MainLayoutContext value={ctx}>
      <AppBarHeadingProvider>{children}</AppBarHeadingProvider>
    </MainLayoutContext>
  );
};
